import { useState } from 'react';

import { THUMB_SIZE } from '@/constants/mediaDisplay';
import { Posts } from '@/models/posts';

type MediaElement = HTMLImageElement | HTMLVideoElement;
type MediaType = 'image' | 'video';

interface Size {
  width: number;
  height: number;
}

interface LoadResult {
  src: string;
  size: Size | null;
}

const getMediaSize = (el: MediaElement): Size =>
  el instanceof HTMLVideoElement
    ? { width: el.videoWidth, height: el.videoHeight }
    : { width: el.naturalWidth, height: el.naturalHeight };

const getSrc = (mediaUrl?: string, timestamp?: number) =>
  mediaUrl && timestamp !== undefined
    ? `${mediaUrl}#t=${timestamp / 1000}`
    : mediaUrl;

const getCrop = (
  { top, left, width, height }: Posts.Coordinates,
  { width: nw, height: nh }: Size
) => {
  const isNorm = width <= 1 && height <= 1;

  const rx = Math.max(0, isNorm ? left * nw : left);
  const ry = Math.max(0, isNorm ? top * nh : top);
  const rw = Math.max(1, Math.min(isNorm ? width * nw : width, nw - rx));
  const rh = Math.max(1, Math.min(isNorm ? height * nh : height, nh - ry));

  const scale = Math.min(1, THUMB_SIZE / rw, THUMB_SIZE / rh);

  return {
    boxSx: {
      position: 'relative',
      overflow: 'hidden',
      width: rw * scale,
      height: rh * scale
    },
    mediaSx: {
      position: 'absolute',
      left: -rx * scale,
      top: -ry * scale,
      width: nw * scale,
      height: nh * scale,
      maxWidth: 'none',
      pointerEvents: 'none'
    }
  } as const;
};

export const useThumbnail = (
  mediaUrl: string | undefined,
  coordinates: Posts.Coordinates,
  mediaType: MediaType,
  timestamp?: number
) => {
  const [result, setResult] = useState<LoadResult | null>(null);

  const isVideo = mediaType === 'video';
  const src = getSrc(mediaUrl, isVideo ? timestamp : undefined);

  const current = result?.src === src ? result : null;
  const size = current?.size ?? null;

  const settle = (nextSize: Size | null) =>
    src && setResult({ src, size: nextSize });

  const onSize = ({ currentTarget }: { currentTarget: MediaElement }) => {
    const mediaSize = getMediaSize(currentTarget);
    settle(mediaSize.width && mediaSize.height ? mediaSize : null);
  };

  return {
    isVideo,
    hasError: !!current && !current.size,
    crop: size ? getCrop(coordinates, size) : null,
    mediaProps: {
      src,
      onLoad: onSize,
      onLoadedData: onSize,
      onError: () => settle(null),
      ...(isVideo && {
        muted: true,
        playsInline: true,
        preload: 'metadata' as const
      })
    }
  };
};
